import { useTranslation } from 'react-i18next'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { apiVisitEngine } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/query-keys'
import type {
  IDonorLabAdmissionDecisionPayload,
  IDonorLabAdmissionDecisionResponse,
  IDonorLabStudiesParams,
  IDonorLabStudiesResponse,
} from '../model/lab-studies.types'

// ── Raw API ───────────────────────────────────────────────────────────────────

export const donorLabStudiesApi = {
  getLabStudies: async ({
    donor_id,
    filter,
    page,
    size,
  }: IDonorLabStudiesParams): Promise<IDonorLabStudiesResponse> => {
    const query = new URLSearchParams()
    query.set('page', String(page))
    query.set('size', String(size))
    if (filter) query.set('filter', filter)

    const response = await apiVisitEngine.get<IDonorLabStudiesResponse>(
      `/v1/donor-card/${donor_id}/lab-studies?${query.toString()}`,
    )
    return response.data
  },

  setAdmissionDecision: async (
    donor_id: string,
    payload: IDonorLabAdmissionDecisionPayload,
  ): Promise<IDonorLabAdmissionDecisionResponse> => {
    const response =
      await apiVisitEngine.post<IDonorLabAdmissionDecisionResponse>(
        `/v1/donor-card/${donor_id}/lab-studies/admission-decision`,
        payload,
        { _skipGlobalErrorHandler: true } as any,
      )
    return response.data
  },
}

// ── Query hooks ───────────────────────────────────────────────────────────────

export const useGetDonorLabStudiesQuery = (params: IDonorLabStudiesParams) => {
  const { i18n } = useTranslation()

  return useQuery({
    // Labels (status, admission, laboratory) are localized server-side via
    // Accept-Language, so the language belongs in the cache key.
    queryKey: [...queryKeys.donorCard.labStudies({ ...params }), i18n.language],
    queryFn: () => donorLabStudiesApi.getLabStudies(params),
    enabled: !!params.donor_id,
    placeholderData: keepPreviousData,
  })
}

// ── Mutation hooks ────────────────────────────────────────────────────────────

/**
 * Admits the donor to donation or defers them off the back of the lab results.
 * The verdict moves the visit forward, so the donor card and the donors list
 * are refreshed alongside the lab studies themselves.
 */
export const useSetDonorLabAdmissionDecisionMutation = (donorId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: IDonorLabAdmissionDecisionPayload) =>
      donorLabStudiesApi.setAdmissionDecision(donorId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.donorCard.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.donors.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.deferrals.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.visits.admitted() })
    },
  })
}
